(() => {
  const currentUrl = new URL(window.location.href);
  const clickToken = currentUrl.searchParams.get('dgwa_click');
  if (!clickToken) return;

  const storageKey = `diggnit-whatsapp-restock-click:${clickToken}`;
  if (!window.sessionStorage.getItem(storageKey)) {
    window.sessionStorage.setItem(storageKey, 'recorded');
    fetch(
      `https://diggnitcustomersupport.floot.app/_api/whatsapp-restock-click?token=${encodeURIComponent(
        clickToken
      )}`,
      {
        method: 'GET',
        mode: 'no-cors',
        credentials: 'omit',
        keepalive: true,
        referrerPolicy: 'no-referrer',
      }
    ).catch(() => {
      window.sessionStorage.removeItem(storageKey);
    });
  }

  currentUrl.searchParams.delete('dgwa_click');
  window.history.replaceState({}, '', currentUrl.toString());
})();

if (!window.DiggnWhatsappCapture) {
  window.DiggnWhatsappCapture = {
    endpoint: 'https://diggnitcustomersupport.floot.app/_api/whatsapp-capture-event',
    send(payload, { once = false } = {}) {
      const normalizedPayload = {
        ...payload,
        locale: String(payload.locale || '').toLowerCase().startsWith('ar') ? 'ar' : 'en',
      };
      const storageKey = `diggnit-whatsapp-capture:${[
        normalizedPayload.flow,
        normalizedPayload.event,
        normalizedPayload.surface,
        normalizedPayload.locale,
        normalizedPayload.productId || '',
        normalizedPayload.variantId || '',
      ].join(':')}`;

      if (once) {
        try {
          if (window.sessionStorage.getItem(storageKey)) return;
          window.sessionStorage.setItem(storageKey, 'recorded');
        } catch {
          // Aggregate telemetry must never block the customer flow.
        }
      }

      fetch(this.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        credentials: 'omit',
        keepalive: true,
        referrerPolicy: 'strict-origin',
        headers: {
          'Content-Type': 'text/plain;charset=UTF-8',
        },
        body: JSON.stringify(normalizedPayload),
      }).catch(() => {
        if (once) {
          try {
            window.sessionStorage.removeItem(storageKey);
          } catch {
            // Aggregate telemetry must never block the customer flow.
          }
        }
      });
    },
  };
}

if (!customElements.get('restock-alert')) {
  customElements.define(
    'restock-alert',
    class RestockAlert extends HTMLElement {
      constructor() {
        super();
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onPhoneEntry = this.onPhoneEntry.bind(this);
        this.onWhatsappOptInChange = this.onWhatsappOptInChange.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.endpoint = this.dataset.endpoint;
        this.productId = this.dataset.productId;
        this.productHandle = this.dataset.productHandle;
        this.productTitle = this.dataset.productTitle || '';
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';

        this.trigger = this.querySelector('[data-restock-trigger]');
        this.panel = this.querySelector('[data-restock-panel]');
        this.form = this.querySelector('[data-restock-form]');
        this.emailInput = this.querySelector('[data-restock-email]');
        this.phoneInput = this.querySelector('[data-restock-phone]');
        this.whatsappOptInInput = this.querySelector('[data-restock-whatsapp-opt-in]');
        this.whatsappConsentElement = this.querySelector('[data-restock-whatsapp-consent]');
        this.message = this.querySelector('[data-restock-message]');
        this.submitButton = this.querySelector('[data-restock-submit]');
        this.submitText = this.querySelector('[data-restock-submit-text]');
        this.formDescription = this.querySelector('[data-restock-description]');
        this.variantSummary = this.querySelector('[data-restock-variant]');
        this.variantSummaryText = this.querySelector('[data-restock-variant-text]');
        this.stringsElement = this.querySelector('[data-restock-strings]');

        this.strings = this.getStrings();
        this.isSubmitting = false;

        if (this.trigger) {
          this.trigger.addEventListener('click', this.onTriggerClick);
        }

        if (this.form) {
          this.form.addEventListener('submit', this.onFormSubmit);
        }
        if (this.phoneInput) {
          this.phoneInput.addEventListener('focus', this.onPhoneEntry);
        }
        if (this.whatsappOptInInput) {
          this.whatsappOptInInput.addEventListener('change', this.onWhatsappOptInChange);
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.resetForVariantChange({ preserveEmail: true });
        this.syncAvailabilityFromDom();
      }

      disconnectedCallback() {
        if (this.trigger) {
          this.trigger.removeEventListener('click', this.onTriggerClick);
        }

        if (this.form) {
          this.form.removeEventListener('submit', this.onFormSubmit);
        }
        if (this.phoneInput) {
          this.phoneInput.removeEventListener('focus', this.onPhoneEntry);
        }
        if (this.whatsappOptInInput) {
          this.whatsappOptInInput.removeEventListener('change', this.onWhatsappOptInChange);
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      getStrings() {
        if (!this.stringsElement) {
          return {};
        }

        try {
          return JSON.parse(this.stringsElement.textContent);
        } catch (error) {
          console.error('Failed to parse restock alert strings', error);
          return {};
        }
      }

      normalizeVariantTitle(title) {
        if (!title || title === 'Default Title') {
          return '';
        }

        return title;
      }

      formatString(template, replacements = {}) {
        if (!template) return '';

        return Object.entries(replacements).reduce(
          (output, [key, value]) => output.replaceAll(`[${key}]`, value || ''),
          template
        );
      }

      getSelectedLabel() {
        return this.normalizeVariantTitle(this.dataset.variantTitle) || '';
      }

      recordCapture(event, reason, once = false) {
        const productId = String(this.productId || '');
        const variantId = String(this.dataset.variantId || '');
        window.DiggnWhatsappCapture.send(
          {
            flow: 'restock',
            event,
            surface: 'product_restock',
            locale: this.locale,
            ...(productId ? { productId } : {}),
            ...(variantId ? { variantId } : {}),
            ...(reason ? { reason } : {}),
          },
          { once }
        );
      }

      onPhoneEntry() {
        this.recordCapture('phone_entry_started', undefined, true);
      }

      onWhatsappOptInChange() {
        if (this.whatsappOptInInput && this.whatsappOptInInput.checked) {
          this.recordCapture('checkbox_selected', undefined, true);
        }
      }

      updateCopy() {
        const selectedLabel = this.getSelectedLabel();

        if (this.variantSummary) {
          this.variantSummary.hidden = !selectedLabel;
        }

        if (this.variantSummaryText) {
          this.variantSummaryText.textContent = selectedLabel;
        }

        if (this.formDescription) {
          if (selectedLabel && this.strings.description_with_variant) {
            this.formDescription.textContent = this.formatString(this.strings.description_with_variant, {
              variant: selectedLabel,
            });
          } else {
            this.formDescription.textContent = this.strings.description || '';
          }
        }
      }

      getCurrentAddButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      getCurrentVariantId() {
        const variantInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        return variantInput ? variantInput.value : '';
      }

      syncAvailabilityFromDom() {
        const addButton = this.getCurrentAddButton();
        const currentVariantId = this.getCurrentVariantId();

        this.dataset.variantId = currentVariantId || this.dataset.variantId || '';
        this.isUnavailable = addButton ? addButton.hasAttribute('disabled') : this.dataset.available !== 'true';

        this.render();
      }

      render() {
        const hasVariant = Boolean(this.dataset.variantId);
        const shouldShow = this.isUnavailable && hasVariant;

        this.hidden = !shouldShow;

        if (!shouldShow) {
          if (this.panel) this.panel.hidden = true;
          if (this.trigger) {
            this.trigger.hidden = true;
            this.trigger.setAttribute('aria-expanded', 'false');
          }
          return;
        }

        if (this.trigger) {
          this.trigger.hidden = false;
        }

        this.recordCapture('impression', undefined, true);
        this.updateCopy();
      }

      resetForVariantChange({ preserveEmail = false, preservePanelState = false } = {}) {
        const emailValue = preserveEmail && this.emailInput ? this.emailInput.value : '';
        const phoneValue = preserveEmail && this.phoneInput ? this.phoneInput.value : '';
        const whatsappOptInValue = preserveEmail && this.whatsappOptInInput ? this.whatsappOptInInput.checked : false;
        const wasOpen = preservePanelState && this.panel ? !this.panel.hidden : false;

        if (this.form) {
          this.form.hidden = false;
          this.form.reset();
        }

        if (this.emailInput && emailValue) {
          this.emailInput.value = emailValue;
        }
        if (this.phoneInput && phoneValue) {
          this.phoneInput.value = phoneValue;
        }
        if (this.whatsappOptInInput) {
          this.whatsappOptInInput.checked = whatsappOptInValue;
        }

        if (this.panel) {
          this.panel.hidden = !wasOpen;
        }

        if (this.trigger) {
          this.trigger.hidden = false;
          this.trigger.setAttribute('aria-expanded', String(wasOpen));
        }

        this.clearMessage();
        this.setLoading(false);
        this.updateCopy();
      }

      onTriggerClick() {
        if (!this.panel || !this.trigger) return;

        const isOpen = !this.panel.hidden;
        this.panel.hidden = isOpen;
        this.trigger.setAttribute('aria-expanded', String(!isOpen));

        if (!isOpen && this.emailInput) {
          this.emailInput.focus();
        }
      }

      onVariantChange(event) {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) {
          return;
        }

        const variant = event.data.variant;
        const updatedHtml = event.data.html;
        const updatedAddButton = updatedHtml
          ? updatedHtml.getElementById(`ProductSubmitButton-${this.sectionId}`)
          : null;

        if (variant) {
          this.dataset.variantId = String(variant.id);
          this.dataset.variantTitle = this.normalizeVariantTitle(variant.title);
          this.dataset.variantSku = variant.sku || '';
          this.dataset.available = String(Boolean(variant.available));
        }

        this.isUnavailable = updatedAddButton
          ? updatedAddButton.hasAttribute('disabled')
          : variant
            ? !variant.available
            : false;

        this.resetForVariantChange({ preserveEmail: true, preservePanelState: true });
        this.render();
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;

        if (!this.submitButton) return;

        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', String(isLoading));

        if (this.submitText) {
          this.submitText.textContent = isLoading
            ? this.strings.submitting || 'Submitting...'
            : this.strings.submit_button || 'Notify me';
        }
      }

      clearMessage() {
        if (!this.message) return;

        this.message.hidden = true;
        this.message.textContent = '';
        this.message.dataset.state = '';
      }

      showMessage(message, state) {
        if (!this.message) return;

        this.message.hidden = false;
        this.message.textContent = message;
        this.message.dataset.state = state;
        this.message.setAttribute('tabindex', '-1');
        this.message.focus();
      }

      async onFormSubmit(event) {
        event.preventDefault();

        if (!this.form || !this.emailInput || !this.endpoint || this.isSubmitting) {
          return;
        }

        if (!this.form.reportValidity()) {
          if (this.whatsappOptInInput && this.whatsappOptInInput.checked) {
            this.recordCapture('validation_failed', 'browser_validation');
          }
          return;
        }

        this.clearMessage();
        this.setLoading(true);

        const payload = {
          email: this.emailInput.value.trim(),
          productId: this.productId,
          variantId: this.dataset.variantId,
          productHandle: this.productHandle,
          productTitle: this.productTitle || undefined,
          variantTitle: this.dataset.variantTitle || undefined,
          sku: this.dataset.variantSku || undefined,
          locale: this.locale,
          requestedQuantity: 1,
          source: 'storefront_button',
          channel: 'email',
          pageUrl: window.location.href,
          phone: this.phoneInput && this.phoneInput.value.trim() ? this.phoneInput.value.trim() : undefined,
          whatsappOptIn: Boolean(this.whatsappOptInInput && this.whatsappOptInInput.checked),
          whatsappConsentText: this.whatsappConsentElement ? this.whatsappConsentElement.textContent.trim() : undefined,
          whatsappConsentVersion: '2026-07-30-restock-v1',
          website: this.form.elements.website ? this.form.elements.website.value : '',
        };

        if (payload.whatsappOptIn && !payload.phone) {
          this.recordCapture('validation_failed', 'phone_required');
          this.showMessage(
            this.strings.error_phone_required || 'Enter your phone number to enable WhatsApp updates.',
            'error'
          );
          this.setLoading(false);
          return;
        }

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            body: JSON.stringify(payload),
          });

          const result = await response.json().catch(() => ({
            ok: false,
            status: 'error',
          }));

          switch (result.status) {
            case 'created':
              if (payload.whatsappOptIn) {
                this.recordCapture('submit_succeeded');
              }
              this.showMessage(
                this.getSelectedLabel() && this.strings.success_created_with_variant
                  ? this.formatString(this.strings.success_created_with_variant, {
                      variant: this.getSelectedLabel(),
                    })
                  : this.strings.success_created || 'You are on the list. We will email you when it is back.',
                'success'
              );
              if (this.form) this.form.hidden = true;
              if (this.trigger) this.trigger.hidden = true;
              break;
            case 'already_exists':
              if (payload.whatsappOptIn) {
                this.recordCapture('submit_succeeded');
              }
              this.showMessage(
                this.getSelectedLabel() && this.strings.success_exists_with_variant
                  ? this.formatString(this.strings.success_exists_with_variant, {
                      variant: this.getSelectedLabel(),
                    })
                  : this.strings.success_exists || 'You are already signed up for this variant.',
                'success'
              );
              if (this.form) this.form.hidden = true;
              if (this.trigger) this.trigger.hidden = true;
              break;
            case 'already_in_stock':
              this.showMessage(
                this.strings.error_in_stock || 'This variant is already back in stock.',
                'info'
              );
              break;
            case 'invalid_variant':
              if (payload.whatsappOptIn) {
                this.recordCapture('validation_failed', 'endpoint_error');
              }
              this.showMessage(
                this.strings.error_invalid_variant || 'We could not verify this variant right now. Please try again.',
                'error'
              );
              break;
            case 'invalid_email':
              if (payload.whatsappOptIn) {
                this.recordCapture('validation_failed', 'endpoint_error');
              }
              this.showMessage(
                result.message || this.strings.error_invalid_email || 'Enter a valid email address.',
                'error'
              );
              break;
            case 'rate_limited':
              if (payload.whatsappOptIn) {
                this.recordCapture('validation_failed', 'endpoint_error');
              }
              this.showMessage(
                result.message || this.strings.error_generic || 'Please try again later.',
                'error'
              );
              break;
            default:
              if (payload.whatsappOptIn) {
                this.recordCapture('validation_failed', 'endpoint_error');
              }
              this.showMessage(
                result.message || this.strings.error_generic || 'Something went wrong. Please try again.',
                'error'
              );
              break;
          }
        } catch (error) {
          console.error('Restock signup request failed', error);
          if (this.whatsappOptInInput && this.whatsappOptInInput.checked) {
            this.recordCapture('validation_failed', 'endpoint_error');
          }
          this.showMessage(
            this.strings.error_generic || 'Something went wrong. Please try again.',
            'error'
          );
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
